import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AgGrid from '@components/AgGrid';
import type { Expense, ExpenseFilter } from '../../../types/expense';
import type { Supplier } from '../../../types/supplier';
import type { CategoryOption } from '../../../constants/khaataCategories';
import './Style.scss';
import NoDataFound from '../NoDataFound';
import ImagePreviewModal from '@components/ImagePreviewModal';
import {
    buildExpensePinnedTotalRow,
    getExpenseColDefs,
    type ExpenseGridRow,
} from './expenseColDefs';

interface ExpenseListProps {
    expenses: Expense[];
    totalExpenseCount: number;
    isLoading: boolean;
    filter: ExpenseFilter;
    suppliers?: Supplier[];
    categoryOptions?: CategoryOption[];
    onEdit: (expense: Expense) => void;
    onDelete: (expenseId: string) => void;
}

const getExpenseRowId = (params: { data: ExpenseGridRow }) => params.data.id;

const GRID_HEIGHT = 400;

const ExpenseListComponent: React.FC<ExpenseListProps> = ({
    expenses,
    totalExpenseCount,
    isLoading,
    filter: _filter,
    suppliers = [],
    categoryOptions,
    onEdit,
    onDelete,
}) => {
    const [previewBill, setPreviewBill] = useState<{ url: string; label?: string } | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const hasShownGridRef = useRef(false);

    const suppliersRef = useRef(suppliers);
    suppliersRef.current = suppliers;
    const categoryOptionsRef = useRef(categoryOptions);
    categoryOptionsRef.current = categoryOptions;

    const getSuppliers = useCallback(() => suppliersRef.current, []);
    const getCategoryOptions = useCallback(() => categoryOptionsRef.current, []);

    const totalAmount = useMemo(
        () => expenses.reduce((sum, expense) => sum + expense.amount, 0),
        [expenses]
    );

    const rowData = useMemo<ExpenseGridRow[]>(() => expenses, [expenses]);
    const pinnedTopRowData = useMemo(
        () => (expenses.length ? [buildExpensePinnedTotalRow(totalAmount)] : []),
        [expenses.length, totalAmount]
    );

    const handleViewBill = useCallback((expense: Expense) => {
        if (!expense.billUrl) return;
        setPreviewBill({ url: expense.billUrl, label: expense.description });
    }, []);

    const colDefs = useMemo(
        () =>
            getExpenseColDefs({
                onEdit,
                onDelete,
                onViewBill: handleViewBill,
                isMobile,
                getSuppliers,
                getCategoryOptions,
            }),
        [onEdit, onDelete, handleViewBill, isMobile, getSuppliers, getCategoryOptions]
    );

    const defaultColDef = useMemo(
        () => ({
            sortable: true,
            resizable: !isMobile,
            suppressMovable: isMobile,
            unSortIcon: true,
        }),
        [isMobile]
    );

    if (totalExpenseCount > 0 || expenses.length > 0) {
        hasShownGridRef.current = true;
    }

    const showInitialLoader = isLoading && !hasShownGridRef.current;
    const showTrulyEmpty = !isLoading && totalExpenseCount === 0;

    if (showInitialLoader) {
        return (
            <Box className="expense-list expense-list--loading">
                <CircularProgress />
            </Box>
        );
    }

    if (showTrulyEmpty) {
        return <NoDataFound title="No Expenses Yet – Start Adding One!" />;
    }

    return (
        <Box className="expense-list">
            {expenses.length === 0 && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5, px: 0.5 }}
                >
                    No entries match the current filters.
                </Typography>
            )}
            <div
                className={`expense-list__grid ${isMobile ? 'expense-list__grid--mobile' : ''}`}
                style={{ height: isMobile ? undefined : GRID_HEIGHT }}
            >
                <AgGrid
                    rowData={rowData}
                    colDefs={colDefs}
                    defaultColDef={defaultColDef}
                    pinnedTopRowData={pinnedTopRowData}
                    domLayout={isMobile ? 'autoHeight' : undefined}
                    suppressRowClickSelection
                    suppressCellFocus
                    animateRows={false}
                    enableCellTextSelection
                    getRowId={getExpenseRowId}
                />
            </div>

            <ImagePreviewModal
                open={!!previewBill}
                images={previewBill?.url ? [previewBill.url] : []}
                onClose={() => setPreviewBill(null)}
                titleLabel={previewBill?.label || 'Bill Preview'}
            />
        </Box>
    );
};

export const ExpenseList = React.memo(ExpenseListComponent);
